import { useEffect, useState } from "react";
import SystemModal from "../Common/SystemModal";
import toast, { Toaster } from "react-hot-toast";
import ajaxSupplierInvoices from "../../util/remote/ajaxSupplierInvoicesPayments";

const SupplierInvoiceDetailsModal = ({ invoiceId, onClose, org }) => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch invoice details by ID
  const fetchDetails = async () => {
    if (!invoiceId) return;
    setLoading(true);
    try {
      const server_response =
        await ajaxSupplierInvoices.fetchSupplierInvoicePaymentsDetails({
          invoID: invoiceId,
        });
      if (server_response.status === "OK") {
        setDetails(server_response.details ?? {});
      } else {
        toast.error(
          server_response.message || "Failed to fetch invoice details"
        );
      }
    } catch (err) {
      console.error(err);
      toast.error("Error fetching invoice details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails();
  }, [invoiceId]);

  if (loading || !details) return <div>Loading...</div>;

  const delivery = details.delivery_items ?? {};
  const statement = details.invoice_statement ?? [];
  const payments = details.payments ?? [];

  // Organization info for header
  const orgInfo = {
    name: "LA_PROCURE AFRICA LIMITED",
    lines: [
      "P.O.BOX 28775 KAMPALA",
      "PLOT 79B BOMBO ROAD",
      "TEL: +256701666160",
      "EMAIL: info@laprocure.africa",
    ],
    logoSrc: `${process.env.PUBLIC_URL}/assets/img/brand/icon-light.png`,
    watermarkSrc: `${process.env.PUBLIC_URL}/assets/img/brand/icon-light.png`,
    ...org,
  };

  // Preload logo & watermark before printing
  const preloadImages = async () => {
    const images = [orgInfo.logoSrc, orgInfo.watermarkSrc];
    return Promise.all(
      images.map(
        (src) =>
          new Promise((res) => {
            const img = new Image();
            img.src = src;
            img.onload = res;
            img.onerror = res;
          })
      )
    );
  };

  const Print = async (e) => {
    e.preventDefault();
    await preloadImages();
    const html = document.getElementById("printablediv").innerHTML;
    const w = window.open("", "printWindow");
    w.document.open();
    w.document.write(`
      <html>
        <head>
          <title>Invoice Details</title>
          <style>${baseStyles(orgInfo.watermarkSrc)}</style>
        </head>
        <body>${html}</body>
      </html>
    `);
    w.document.close();
    w.focus();
    w.print();
    w.onafterprint = () => w.close();
  };

  const RenderFooter = (controls) => (
    <>
      <button className="btn ripple btn-dark" onClick={controls.close}>
        Close
      </button>
      <button className="btn ripple btn-dark" onClick={Print}>
        Print
      </button>
    </>
  );

  return (
    <SystemModal
      title={`Invoice Details: ${details.invoice_no}`}
      id="modal-invoice-details"
      size="xl"
      footer={RenderFooter}
    >
      <Toaster />
      <style>{baseStyles(orgInfo.watermarkSrc)}</style>
      <div id="printablediv">
        <div className="doc">
          <div className="doc-content">
            {/* Header */}
            <div className="doc-header mb-3">
              <div className="logo-wrap">
                <img src={orgInfo.logoSrc} alt="Logo" />
              </div>
              <div className="org-text">
                <div className="org-name">{orgInfo.name}</div>
                {orgInfo.lines.map((line, i) => (
                  <div key={i} className="org-line">
                    {line}
                  </div>
                ))}
              </div>
            </div>

            {/* Invoice Info */}
            <div className="invoice-info mb-3">
              <h5>Supplier: {details.supplier_name}</h5>
              <p>
                Contact: {details.buss_contact} | Email: {details.buss_email}
              </p>
              <p>
                Invoice No: <b>{details.invoice_no}</b>
              </p>
              <p>Invoice Amount: {details.amount}</p>
              <p>Invoice Balance: {details.balance}</p>
              <p>Status: {details.status === "0" ? "Pending" : "Completed"}</p>
              <p>Date Created: {details.date_created}</p>
            </div>

            {/* Delivery Items */}
            <div className="delivery-section mb-3">
              <h6>Delivery Details</h6>
              <p>Delivery Ref: {delivery.delievery_reff_no}</p>
              <p>Delivered By: {delivery.Delivered_by || "Pending"}</p>
              <p>Date Created: {delivery.date_created}</p>
              <p>Comment: {delivery.comment}</p>
              <table className="table mt-2">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Item</th>
                    <th>Quantity</th>
                  </tr>
                </thead>
                <tbody>
                  {delivery.items?.map((it, idx) => (
                    <tr key={it.id}>
                      <td>{idx + 1}</td>
                      <td>{it.item_name}</td>
                      <td>{it.quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Invoice Statement */}
            <div className="statement-section mb-3">
              <h6>Invoice Statement</h6>
              <table className="table mt-2">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Date</th>
                    <th>Credit</th>
                    <th>Debit</th>
                    <th>Balance</th>
                    <th>Ref</th>
                  </tr>
                </thead>
                <tbody>
                  {statement.map((st, idx) => (
                    <tr key={st.id}>
                      <td>{idx + 1}</td>
                      <td>{st.date}</td>
                      <td>{st.credit}</td>
                      <td>{st.debit}</td>
                      <td>{st.balance}</td>
                      <td>{st.external_reff}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Payments Section */}
            <div className="payments-section mb-3">
              <h6>Payments</h6>
              <table className="table mt-2">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Method</th>
                    <th>Check No</th>
                    <th>Account No</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p, idx) => (
                    <tr key={p.id}>
                      <td>{idx + 1}</td>
                      <td>{p.date_created}</td>
                      <td>{p.amount}</td>
                      <td>{p.payment_method}</td>
                      <td>{p.check_number || "-"}</td>
                      <td>{p.account_number || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Signature */}
            <div className="signature mt-4">
              <p>
                <b>Authorized By:</b>
              </p>
              <div className="line"></div>
            </div>
          </div>
          <div className="footer-print mt-3">
            Printed on: {new Date().toLocaleString()}
          </div>
        </div>
      </div>
    </SystemModal>
  );
};

// Beautified CSS for modal + print
const baseStyles = (watermarkSrc) => `
* { box-sizing: border-box; }
body { font-family: "Segoe UI", Arial, sans-serif; margin: 0; padding: 0; background: #f9fafb; }
.doc {
  position: relative;
  padding: 20mm 18mm;
  min-width: 210mm; max-width: 210mm;
  margin: 0 auto;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  display: flex; flex-direction: column; min-height: 100%;
}
.doc::before {
  content: "";
  position: absolute; inset: 0;
  background: url("${watermarkSrc}") no-repeat center center;
  background-size: 65%;
  opacity: 0.08;
  z-index: 0;
}
.doc-content { flex: 1; display: flex; flex-direction: column; position: relative; z-index: 1; }

.doc-header {
  display: flex; align-items: center; justify-content: space-between;
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 10px; margin-bottom: 20px;
}
.logo-wrap img { width: 90px; height: 90px; object-fit: contain; }
.org-text { flex: 1; text-align: center; }
.org-name { font-size: 20px; font-weight: 700; letter-spacing: 0.5px; color: #1f2937; }
.org-line { font-size: 12px; line-height: 1.4; color: #4b5563; }

.invoice-info {
  background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 8px;
  padding: 12px 16px; margin-bottom: 16px;
}
.invoice-info h5 { font-size: 15px; margin-bottom: 6px; font-weight: 600; color: #111827; }
.invoice-info p { margin: 2px 0; font-size: 13px; color: #374151; }

h6 {
  margin: 8px 0;
  font-size: 14px; font-weight: 600;
  background: #f3f4f6; padding: 6px 10px;
  border-left: 3px solid #2563eb;
  color: #111827; border-radius: 4px;
}

table { border-collapse: collapse; width: 100%; margin-top: 8px; }
th, td {
  border: 1px solid #d1d5db;
  padding: 6px 10px;
  font-size: 12px;
}
th {
  background: #f3f4f6;
  font-weight: 600;
  text-align: left;
  position: sticky; top: 0;
  z-index: 1;
}
tbody tr:nth-child(even) { background: #f9fafb; }
tbody tr:hover { background: #eef6ff; }

.signature {
  margin-top: 28px;
}
.signature p { font-size: 13px; margin-bottom: 6px; }
.signature .line {
  margin-top: 14px;
  border-bottom: 1px solid #111;
  width: 55%; height: 20px;
}

.footer-print {
  margin-top: 18px;
  font-size: 11px;
  text-align: right;
  color: #6b7280;
  border-top: 1px solid #e5e7eb;
  padding-top: 6px;
}

@page { size: A4; margin: 12mm; }
`;

export default SupplierInvoiceDetailsModal;
